import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { compose, flexbox, grid, layout, space } from 'styled-system';

import { forwardValidProp } from '../../utils/style';

const getColumnCSSValue = (columns) => {
  if (Number.isInteger(columns)) {
    return `repeat(${columns}, 1fr)`;
  }

  if (typeof columns === 'string') {
    return columns;
  }

  return false;
};

const baseStyles = () => css`
  display: grid;
  grid-template-rows: auto;
  flex: 1;
  width: 100%;
`;

const columnsStyles = ({ theme, gridTemplateColumns }) => css`
  grid-template-columns: ${getColumnCSSValue(gridTemplateColumns || 4)};

  ${theme.mediaQueries.md} {
    grid-template-columns: ${getColumnCSSValue(gridTemplateColumns || 12)};
  }

  ${theme.mediaQueries.xl} {
    grid-template-columns: ${getColumnCSSValue(gridTemplateColumns || 16)};
  }
`;

const gapStyles = ({ theme, gridColumnGap }) => css`
  grid-gap: ${gridColumnGap && theme.space[gridColumnGap]};
`;

const StyledGrid = styled('div', {
  shouldForwardProp: (prop) => forwardValidProp(prop),
})`
  ${baseStyles}
  ${columnsStyles}
  ${gapStyles}
  ${compose(flexbox, grid, layout, space)}
`;

const Grid = forwardRef(({ ...props }, ref) => (
  <StyledGrid ref={ref} {...props} />
));

Grid.displayName = 'Grid';

Grid.defaultProps = {
  gridColumnGap: 4,
  gridAutoFlow: 'row',
};

Grid.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.array,
    PropTypes.func,
  ]).isRequired,
  /**
   * Custom grid flow. Can be any valid grid `grid-template-rows` css property
   */
  gridAutoFlow: PropTypes.string,
  gridColumnGap: PropTypes.number,
  /**
   * Implements `styled-system` [grid](https://styled-system.com/api/#grid) utility
   */
  ...propTypes.grid,
  /**
   * Implements `styled-system` [flexbox](https://styled-system.com/api/#flexbox) utility
   */
  ...propTypes.flexbox,
  /**
   * Implements `styled-system` [layout](https://styled-system.com/api/#layout) utility
   */
  ...propTypes.layout,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
};

export default Grid;
